import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBarcode,
  faFileArrowUp,
  faFileLines,
  faFloppyDisk,
  faLocationArrow,
  faPenToSquare,
  faSpinner,
  faTrash,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import InventoryAddFlow, { type InventoryAddDraft } from './InventoryAddFlow'
import InventoryCheckTo from './InventoryCheckTo'
import AssetFormFields from './AssetFormFields'
import TallyItemView from './TallyItemView'
import PhotoUploadPreview from './PhotoUploadPreview'
import PhotoGalleryFrame from './PhotoGalleryFrame'
import JobSearchField from './JobSearchField'
import type {
  AssetOrInventory,
  EmployeeAssignmentOption,
  InventoryHistoryEntry,
  JobTreadJobOption,
  TallyModalMode,
} from './types'
import {
  DOCUMENT_ACCEPT,
  formatFileSize,
  formatInventoryStatus,
  getDocuments,
  getImageUrls,
  INVENTORY_STATUSES,
  jobOptionFromItem,
  locationAddressFromJob,
} from './types'
import { useAutoDismiss } from './useAutoDismiss'

type TallyItemModalProps = {
  open: boolean
  item: AssetOrInventory | null
  editingItem: AssetOrInventory | null
  mode: TallyModalMode
  canManage: boolean
  employeeOptions: EmployeeAssignmentOption[]
  getAssignmentLabel: (item: AssetOrInventory) => string | null
  modalMessage: string | null
  saving: boolean
  deleting: boolean
  inventoryAddDraft?: InventoryAddDraft | null
  onInventoryAddDraftChange?: (
    patch: Partial<InventoryAddDraft> | ((prev: InventoryAddDraft) => Partial<InventoryAddDraft>),
  ) => void
  onOpenBarcodeScanner?: () => void
  showCheckTo: boolean
  onShowCheckTo: (show: boolean) => void
  onCheckTo: (job: JobTreadJobOption) => Promise<void>
  history: InventoryHistoryEntry[]
  historyLoading: boolean
  modalTab: 'info' | 'history'
  onModalTabChange: (tab: 'info' | 'history') => void
  galleryIndex: number
  onGalleryIndexChange: (index: number) => void
  onClose: () => void
  onEdit: () => void
  onSave: () => void
  onDelete: () => void
  onUpdateField: (key: keyof AssetOrInventory, value: unknown) => void
  onUpdateLocationAddress: (address: string) => void
  onApplyJobLocation?: (job: JobTreadJobOption | null) => void
  onUploadImages: (files: FileList | null) => void
  onAddImageDataUrls?: (dataUrls: string[]) => void
  onRemoveImage: (idx: number) => void
  onUploadDocuments?: (files: FileList | null) => void
  onRemoveDocument?: (idx: number) => void
}

function formatHistoryJob(job?: { jobId?: string; jobNumber?: string; jobName?: string }) {
  if (!job) return '—'
  if (job.jobNumber && job.jobName) return `${job.jobNumber} — ${job.jobName}`
  return job.jobName || job.jobNumber || job.jobId || '—'
}

export const TallyItemModal: React.FC<TallyItemModalProps> = ({
  open,
  item,
  editingItem,
  mode,
  canManage,
  employeeOptions,
  getAssignmentLabel,
  modalMessage,
  saving,
  deleting,
  inventoryAddDraft,
  onInventoryAddDraftChange,
  onOpenBarcodeScanner,
  showCheckTo,
  onShowCheckTo,
  onCheckTo,
  history,
  historyLoading,
  modalTab,
  onModalTabChange,
  galleryIndex,
  onGalleryIndexChange,
  onClose,
  onEdit,
  onSave,
  onDelete,
  onUpdateField,
  onUpdateLocationAddress,
  onApplyJobLocation,
  onUploadImages,
  onAddImageDataUrls,
  onRemoveImage,
  onUploadDocuments,
  onRemoveDocument,
}) => {
  const [localMsgDismissed, setLocalMsgDismissed] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  useEffect(() => {
    setLocalMsgDismissed(false)
  }, [modalMessage])
  useAutoDismiss(modalMessage && !localMsgDismissed ? modalMessage : null, () => setLocalMsgDismissed(true))
  useEffect(() => {
    if (!open) setConfirmDeleteOpen(false)
  }, [open])

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  if (!open || !item) return null

  const displayItem = mode === 'view' ? item : editingItem || item
  const images = getImageUrls(displayItem)
  const documents = getDocuments(displayItem)
  const isInventoryAdd = mode === 'add' && item.source === 'Inventory'
  const isAssetForm = mode !== 'view' && editingItem?.source === 'Asset'
  const hideTopMedia = mode === 'add' || isAssetForm || mode === 'view'
  const showSaveFooter = canManage && mode !== 'view'
  const showCheckToInFooter =
    mode === 'view' &&
    !showCheckTo &&
    item.source === 'Inventory' &&
    modalTab === 'info'
  const showViewFooter = mode === 'view' && !showCheckTo && (canManage || showCheckToInFooter)
  const title =
    mode === 'add'
      ? item.source === 'Inventory'
        ? 'Add inventory'
        : 'Add asset'
      : item.name || 'Details'

  return createPortal(
    <div
      className="fixed inset-0 z-[999] flex items-stretch justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative flex h-[100dvh] max-h-[100dvh] w-full min-h-0 max-w-none flex-col overflow-hidden rounded-none bg-base-100 shadow-2xl sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:max-w-2xl sm:rounded-box"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="relative shrink-0 border-b border-base-200 px-5 pt-5 pb-3">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-bold text-xl leading-tight pr-8">{title}</h3>
            <button
              type="button"
              className="btn btn-sm btn-circle btn-ghost absolute right-3 top-3"
              onClick={onClose}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          {modalMessage && !localMsgDismissed ? (
            <div className="alert alert-info mt-3 py-2 text-sm">
              <span className="flex-1">{modalMessage}</span>
              <button
                type="button"
                className="btn btn-ghost btn-xs"
                onClick={() => setLocalMsgDismissed(true)}
                aria-label="Dismiss"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          ) : null}
        </div>

        {!hideTopMedia ? (
          <div className="shrink-0 px-5 pt-4 flex flex-col items-center">
            {canManage && mode === 'edit' && item.source === 'Inventory' ? (
              <PhotoUploadPreview
                images={images}
                alt={item.name}
                variant="wide"
                desktopControl="file-input"
                galleryIndex={galleryIndex}
                onGalleryIndexChange={onGalleryIndexChange}
                onUpload={(files) => {
                  void onUploadImages(files)
                }}
                onAddDataUrls={onAddImageDataUrls}
                onRemove={onRemoveImage}
              />
            ) : (
              <PhotoGalleryFrame
                images={images}
                index={galleryIndex}
                onIndexChange={onGalleryIndexChange}
                alt={item.name}
                variant="square"
                className="mb-3"
              />
            )}
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain w-full px-5 py-4">
          {isInventoryAdd && inventoryAddDraft && onInventoryAddDraftChange ? (
            <InventoryAddFlow
              draft={inventoryAddDraft}
              onPatch={onInventoryAddDraftChange}
              onOpenScanner={() => onOpenBarcodeScanner?.()}
              saving={saving}
            />
          ) : isAssetForm && editingItem ? (
            <AssetFormFields
              item={editingItem}
              employeeOptions={employeeOptions}
              saving={saving}
              onUpdateField={onUpdateField}
              onUpdateLocationAddress={onUpdateLocationAddress}
              onApplyJobLocation={onApplyJobLocation}
              onUploadImages={onUploadImages}
              onAddImageDataUrls={onAddImageDataUrls}
              onRemoveImage={onRemoveImage}
            />
          ) : mode !== 'view' && editingItem ? (
            <div className="w-full max-w-md mx-auto flex flex-col gap-4">
              <section className="rounded-box border border-base-200 bg-base-200/30 p-3 space-y-3">
                <h4 className="font-semibold text-sm">Details</h4>
                <div className="flex flex-col gap-1.5 w-full">
                  <span className="text-sm font-medium">Name</span>
                  <input
                    className="input input-bordered w-full"
                    value={editingItem.name || ''}
                    onChange={(e) => onUpdateField('name', e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1.5 w-full">
                  <span className="text-sm font-medium">Type</span>
                  <input
                    className="input input-bordered w-full"
                    value={editingItem.type || ''}
                    onChange={(e) => onUpdateField('type', e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1.5 w-full">
                  <span className="text-sm font-medium">Assigned employee</span>
                  <select
                    className="select select-bordered w-full"
                    value={editingItem.assignedEmployeeId || ''}
                    onChange={(e) => onUpdateField('assignedEmployeeId', e.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {employeeOptions.map((employee) => (
                      <option key={employee.employeeId} value={employee.employeeId}>
                        {employee.displayName} ({employee.employeeId})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5 w-full">
                  <span className="text-sm font-medium">Barcode</span>
                  <div className="flex gap-2">
                    <input
                      className="input input-bordered flex-1 font-mono"
                      value={editingItem.barcode || ''}
                      onChange={(e) => onUpdateField('barcode', e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => onOpenBarcodeScanner?.()}
                    >
                      <FontAwesomeIcon icon={faBarcode} />
                    </button>
                  </div>
                  {!editingItem.barcode ? (
                    <span className="text-xs text-warning">Legacy item — add a barcode to enable scan lookup</span>
                  ) : null}
                </div>
                <div className="flex flex-col gap-1.5 w-full">
                  <span className="text-sm font-medium">Quantity</span>
                  <input
                    type="number"
                    className="input input-bordered w-full"
                    value={editingItem.quantity ?? 1}
                    onChange={(e) => onUpdateField('quantity', Number(e.target.value))}
                  />
                </div>
                <div className="flex flex-col gap-1.5 w-full">
                  <span className="text-sm font-medium">SKU</span>
                  <input
                    className="input input-bordered w-full"
                    value={editingItem.sku || ''}
                    onChange={(e) => onUpdateField('sku', e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1.5 w-full">
                  <span className="text-sm font-medium">Value</span>
                  <input
                    type="number"
                    className="input input-bordered w-full"
                    value={editingItem.value ?? 0}
                    onChange={(e) => onUpdateField('value', Number(e.target.value))}
                  />
                </div>
                <div className="flex flex-col gap-1.5 w-full">
                  <span className="text-sm font-medium">Status</span>
                  <select
                    className="select select-bordered w-full"
                    value={editingItem.status || 'in_stock'}
                    onChange={(e) => onUpdateField('status', e.target.value)}
                  >
                    {INVENTORY_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                    {editingItem.status &&
                    !INVENTORY_STATUSES.some((s) => s.value === editingItem.status) ? (
                      <option value={editingItem.status}>
                        {formatInventoryStatus(editingItem.status)}
                      </option>
                    ) : null}
                  </select>
                </div>
                <JobSearchField
                  label="Location"
                  value={jobOptionFromItem(editingItem)}
                  onChange={(job) => {
                    if (onApplyJobLocation) onApplyJobLocation(job)
                    else {
                      onUpdateField('jobId', job?.id || '')
                      onUpdateField('jobNumber', job?.number || '')
                      onUpdateField('jobName', job?.name || '')
                      onUpdateField('project', job?.name || job?.number || '')
                      onUpdateLocationAddress(locationAddressFromJob(job))
                    }
                  }}
                  placeholder="Search JobTread locations..."
                  disabled={saving}
                />
                {editingItem.location?.address ? (
                  <p className="text-xs text-base-content/60">Site: {editingItem.location.address}</p>
                ) : null}
                <div className="flex flex-col gap-1.5 w-full">
                  <span className="text-sm font-medium">Notes</span>
                  <textarea
                    className="textarea textarea-bordered w-full"
                    value={editingItem.notes || ''}
                    onChange={(e) => onUpdateField('notes', e.target.value)}
                  />
                </div>
              </section>

              <section className="rounded-box border border-base-200 bg-base-200/30 p-3 space-y-3">
                <h4 className="font-semibold text-sm">Documents</h4>
                <label className="btn btn-outline btn-sm w-full">
                  <FontAwesomeIcon icon={faFileArrowUp} />
                  Attach documents
                  <input
                    type="file"
                    accept={DOCUMENT_ACCEPT}
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      onUploadDocuments?.(e.target.files)
                      e.currentTarget.value = ''
                    }}
                  />
                </label>
                {documents.length > 0 ? (
                  <ul className="space-y-2">
                    {documents.map((doc, idx) => (
                      <li
                        key={`${doc.fileName}-${idx}`}
                        className="flex items-center gap-2 rounded-box border border-base-300 bg-base-100 px-3 py-2 text-sm"
                      >
                        <FontAwesomeIcon icon={faFileLines} className="text-base-content/50 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{doc.fileName}</div>
                          <div className="text-xs text-base-content/55">{formatFileSize(doc.size)}</div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs btn-square"
                          onClick={() => onRemoveDocument?.(idx)}
                          aria-label={`Remove ${doc.fileName}`}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-base-content/55">No documents attached</p>
                )}
              </section>
            </div>
          ) : showCheckTo && item.source === 'Inventory' ? (
            <InventoryCheckTo item={item} onCancel={() => onShowCheckTo(false)} onConfirm={onCheckTo} />
          ) : (
            <TallyItemView
              item={item}
              images={images}
              documents={documents}
              galleryIndex={galleryIndex}
              onGalleryIndexChange={onGalleryIndexChange}
              getAssignmentLabel={getAssignmentLabel}
              modalTab={modalTab}
              onModalTabChange={item.source === 'Inventory' ? onModalTabChange : undefined}
              historySlot={
                historyLoading ? (
                  <div className="py-10 text-center text-base-content/60">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    Loading history...
                  </div>
                ) : history.length === 0 ? (
                  <div className="py-10 text-center text-base-content/60 italic">No history yet</div>
                ) : (
                  <ul className="timeline timeline-vertical timeline-compact">
                    {history.map((entry, idx) => (
                      <li key={entry._id}>
                        {idx > 0 ? <hr /> : null}
                        <div className="timeline-start text-xs text-base-content/50 whitespace-nowrap">
                          {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : ''}
                        </div>
                        <div className="timeline-middle">
                          <span className="badge badge-primary badge-xs" />
                        </div>
                        <div className="timeline-end timeline-box text-sm mb-3">
                          {entry.action === 'updated' ? (
                            <div className="text-sm text-base-content/80">
                              {entry.note || 'Updated'}
                            </div>
                          ) : (
                            <>
                              <div className="font-medium capitalize">{entry.action.replace('_', ' ')}</div>
                              {entry.action === 'check_to' ? (
                                <div className="text-xs mt-1">
                                  {formatHistoryJob(entry.fromJob)} → {formatHistoryJob(entry.toJob)}
                                </div>
                              ) : null}
                              {entry.note ? (
                                <div className="text-xs mt-1 text-base-content/70">{entry.note}</div>
                              ) : null}
                              {entry.userName ? (
                                <div className="text-xs text-base-content/60 mt-1">by {entry.userName}</div>
                              ) : null}
                            </>
                          )}
                        </div>
                        {idx < history.length - 1 ? <hr /> : null}
                      </li>
                    ))}
                  </ul>
                )
              }
            />
          )}
        </div>

        {showSaveFooter ? (
          <div className="shrink-0 border-t border-base-200 px-5 py-3 flex flex-wrap justify-end gap-2 bg-base-100">
            {canManage ? (
              <button
                type="button"
                className="btn btn-error btn-outline"
                disabled={deleting}
                onClick={() => setConfirmDeleteOpen(true)}
              >
                <FontAwesomeIcon
                  icon={deleting ? faSpinner : faTrash}
                  className={deleting ? 'animate-spin' : ''}
                />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            ) : null}
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={onSave}>
              <FontAwesomeIcon
                icon={saving ? faSpinner : faFloppyDisk}
                className={saving ? 'animate-spin' : ''}
              />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : null}

        {showViewFooter ? (
          <div className="shrink-0 border-t border-base-200 px-5 py-3 flex flex-wrap justify-end gap-2 bg-base-100">
            {canManage ? (
              <button
                type="button"
                className="btn btn-error btn-outline"
                disabled={deleting}
                onClick={() => setConfirmDeleteOpen(true)}
              >
                <FontAwesomeIcon
                  icon={deleting ? faSpinner : faTrash}
                  className={deleting ? 'animate-spin' : ''}
                />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            ) : null}
            {canManage ? (
              <button type="button" className="btn btn-outline" onClick={onEdit}>
                <FontAwesomeIcon icon={faPenToSquare} />
                Edit
              </button>
            ) : null}
            {showCheckToInFooter ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => onShowCheckTo(true)}
              >
                <FontAwesomeIcon icon={faLocationArrow} />
                Check to
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {confirmDeleteOpen ? (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
          onClick={() => {
            if (!deleting) setConfirmDeleteOpen(false)
          }}
          role="presentation"
        >
          <div
            className="w-full max-w-sm rounded-box bg-base-100 p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="tally-delete-title"
          >
            <h3 id="tally-delete-title" className="font-bold text-lg">
              Delete {item.source === 'Asset' ? 'asset' : 'inventory'}?
            </h3>
            <p className="py-3 text-sm">
              Are you sure you want to delete <span className="font-semibold">{item.name || 'this item'}</span>?
              This cannot be undone.
            </p>
            <div className="mt-2 flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={deleting}
                onClick={() => setConfirmDeleteOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-error"
                disabled={deleting}
                onClick={() => {
                  onDelete()
                }}
              >
                <FontAwesomeIcon
                  icon={deleting ? faSpinner : faTrash}
                  className={deleting ? 'animate-spin' : ''}
                />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>,
    document.body,
  )
}

export default TallyItemModal
